import { useState } from "react";
import { FlaskConical, RotateCcw, ChevronUp } from "lucide-react";
import { EMOTIONS } from "./TradeTable";
import { JournalChart } from "./JournalChart";

const DAYS = [
  { value: 1, label: "Lun" }, { value: 2, label: "Mar" }, { value: 3, label: "Mer" },
  { value: 4, label: "Gio" }, { value: 5, label: "Ven" }, { value: 6, label: "Sab" }, { value: 0, label: "Dom" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const initialFilters = { hourFrom: "", hourTo: "", excludedDays: [], direction: "all", excludedEmotions: [] };

const computeStats = (trades) => {
  const wins = trades.filter((t) => t.outcome === "win").length;
  const losses = trades.filter((t) => t.outcome === "loss").length;
  const winRate = wins + losses > 0 ? (wins / (wins + losses)) * 100 : null;
  const cum = trades.reduce((s, t) => s + (parseFloat(t.percentage) || 0), 0);
  return { total: trades.length, wins, losses, winRate, cum };
};

const fmtRate = (r) => (r == null ? "—" : `${r.toFixed(1)}%`);
const fmtCum = (c) => `${c >= 0 ? "+" : ""}${(Math.round(c * 100) / 100)}%`;

export const WhatIf = ({ trades }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(initialFilters);

  if (!trades || trades.length === 0) return null;

  const toggleIn = (key, val) =>
    setF((prev) => ({ ...prev, [key]: prev[key].includes(val) ? prev[key].filter((x) => x !== val) : [...prev[key], val] }));

  const filtered = trades.filter((t) => {
    const d = new Date(t.trade_datetime);
    const hour = d.getHours();
    if ((f.hourFrom !== "" || f.hourTo !== "") && !isNaN(hour)) {
      const from = f.hourFrom !== "" ? parseInt(f.hourFrom) : 0;
      const to = f.hourTo !== "" ? parseInt(f.hourTo) : 23;
      const inRange = from <= to ? hour >= from && hour <= to : hour >= from || hour <= to;
      if (!inRange) return false;
    }
    if (f.excludedDays.length && !isNaN(d.getDay()) && f.excludedDays.includes(d.getDay())) return false;
    if (f.direction !== "all" && (t.direction || "buy") !== f.direction) return false;
    if (f.excludedEmotions.length && (t.emotions || []).some((e) => f.excludedEmotions.includes(e))) return false;
    return true;
  });

  const real = computeStats(trades);
  const sim = computeStats(filtered);
  const hasFilters = f.hourFrom !== "" || f.hourTo !== "" || f.excludedDays.length > 0 || f.direction !== "all" || f.excludedEmotions.length > 0;

  const deltaColor = (simV, realV) => {
    if (simV == null || realV == null) return "var(--mm-text-muted)";
    if (simV > realV) return "#2FD8A8";
    if (simV < realV) return "#EF4444";
    return "var(--mm-text-muted)";
  };

  const pill = (active, color = "#EF4444") => ({
    border: `1px solid ${active ? color : "var(--mm-border)"}`,
    color: active ? color : "var(--mm-text-secondary)",
    backgroundColor: active ? "rgba(239,68,68,0.1)" : "transparent",
  });

  return (
    <div className="mb-8" data-testid="whatif-section">
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-3 w-full px-5 py-4 transition-colors hover:bg-white/5 glass-card"
        style={{ border: `1px solid ${open ? "var(--mm-gold)" : "var(--mm-border)"}` }}
        data-testid="whatif-toggle-btn">
        <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(100,200,255,0.12)" }}>
          <FlaskConical size={20} style={{ color: "#64C8FF" }} />
        </div>
        <div className="flex-1 text-left">
          <p className="text-sm font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>What If — Simula i tuoi scenari</p>
          <p className="text-xs" style={{ color: "var(--mm-text-muted)" }}>E se avessi tradato solo in certe fasce orarie? Senza i trade in FOMO? Solo Buy? Scoprilo.</p>
        </div>
        <ChevronUp size={18} style={{ color: "var(--mm-text-muted)", transform: open ? "none" : "rotate(180deg)", transition: "transform 0.2s" }} />
      </button>

      {open && (
        <div className="glass-card p-6 sm:p-8 mt-2" data-testid="whatif-panel">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="text-xs uppercase font-semibold block mb-2" style={{ color: "var(--mm-text-muted)", letterSpacing: "0.1em" }}>Fascia oraria (solo trade tra le...)</label>
              <div className="flex items-center gap-2">
                <select value={f.hourFrom} onChange={(e) => setF({ ...f, hourFrom: e.target.value })} className="admin-input" style={{ maxWidth: "130px" }} data-testid="whatif-hour-from">
                  <option value="">Dalle —</option>
                  {HOURS.map((h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
                </select>
                <span style={{ color: "var(--mm-text-muted)" }}>→</span>
                <select value={f.hourTo} onChange={(e) => setF({ ...f, hourTo: e.target.value })} className="admin-input" style={{ maxWidth: "130px" }} data-testid="whatif-hour-to">
                  <option value="">Alle —</option>
                  {HOURS.map((h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:59</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase font-semibold block mb-2" style={{ color: "var(--mm-text-muted)", letterSpacing: "0.1em" }}>Direzione</label>
              <select value={f.direction} onChange={(e) => setF({ ...f, direction: e.target.value })} className="admin-input" style={{ maxWidth: "200px" }} data-testid="whatif-direction">
                <option value="all">Buy + Sell</option>
                <option value="buy">Solo Buy</option>
                <option value="sell">Solo Sell</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase font-semibold block mb-2" style={{ color: "var(--mm-text-muted)", letterSpacing: "0.1em" }}>Escludi i giorni</label>
              <div className="flex flex-wrap gap-2">
                {DAYS.map((d) => (
                  <button key={d.value} onClick={() => toggleIn("excludedDays", d.value)}
                    className="px-3 py-1.5 text-xs font-bold uppercase transition-colors"
                    style={pill(f.excludedDays.includes(d.value), "#EF4444")}
                    data-testid={`whatif-day-${d.value}`}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase font-semibold block mb-2" style={{ color: "var(--mm-text-muted)", letterSpacing: "0.1em" }}>Escludi i trade con emotività</label>
              <div className="flex flex-wrap gap-2">
                {EMOTIONS.map((e) => (
                  <button key={e} onClick={() => toggleIn("excludedEmotions", e)}
                    className="px-2.5 py-1.5 text-xs font-semibold transition-colors"
                    style={pill(f.excludedEmotions.includes(e), "#EF4444")}
                    data-testid={`whatif-emotion-${e.replace(/\s+/g, "-").toLowerCase()}`}>
                    {e}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {hasFilters && (
            <button onClick={() => setF(initialFilters)} className="flex items-center gap-2 text-xs font-semibold mb-6" style={{ color: "var(--mm-gold)" }} data-testid="whatif-reset-btn">
              <RotateCcw size={13} /> Azzera filtri
            </button>
          )}

          <div className="mb-6 pt-6" style={{ borderTop: "1px solid var(--mm-border)" }}>
            <p className="text-sm font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }} data-testid="whatif-count">
              Simulazione su <span className="gold-text">{sim.total}</span> di {real.total} trade
              {hasFilters && sim.total < real.total && <span className="text-xs font-normal ml-2" style={{ color: "var(--mm-text-muted)" }}>({real.total - sim.total} esclusi)</span>}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: "Trade", real: real.total, sim: sim.total, fmt: (v) => v },
                { label: "Win / Loss", real: `${real.wins}/${real.losses}`, sim: `${sim.wins}/${sim.losses}`, fmt: (v) => v, noDelta: true },
                { label: "Win Rate", real: real.winRate, sim: sim.winRate, fmt: fmtRate, num: true },
                { label: "% Cumulata", real: real.cum, sim: sim.total > 0 ? sim.cum : null, fmt: (v) => (v == null ? "—" : fmtCum(v)), num: true },
              ].map((s, i) => (
                <div key={i} className="glass-card p-4" data-testid={`whatif-stat-${i}`}>
                  <p className="text-xs uppercase mb-2" style={{ color: "var(--mm-text-muted)", letterSpacing: "0.1em" }}>{s.label}</p>
                  <p className="text-xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: s.num ? deltaColor(s.sim, s.real) : "var(--mm-text-primary)" }}>{s.fmt(s.sim)}</p>
                  <p className="text-xs mt-1" style={{ color: "var(--mm-text-muted)" }}>Reale: {s.fmt(s.real)}</p>
                </div>
              ))}
            </div>
          </div>

          {sim.total > 0 ? (
            <JournalChart trades={filtered} />
          ) : (
            <p className="text-sm text-center py-6" style={{ color: "var(--mm-text-muted)" }} data-testid="whatif-empty">Nessun trade corrisponde ai filtri selezionati.</p>
          )}
        </div>
      )}
    </div>
  );
};
